import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styles from './MCPChatWidget.module.scss'
import { authenticatedFetch } from '../../services/api/apiClient'
import { apiEndpoints, colors } from '../../consts'

const MCPChatWidget = ({ apiUrl = apiEndpoints.openaiAgent }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([
    {
      id: 1,
      content: 'Hello! I\'m Veta Assistant, your dashboard companion. I can help you analyze campaigns, trends, performance metrics, and answer questions about your data. Try asking me something!',
      isUser: false,
      timestamp: new Date().toISOString()
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const checkConnection = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${apiUrl}/health`)
      setIsConnected(response.ok)
    } catch (error) {
      setIsConnected(false)
    }
  }, [apiUrl])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (isOpen) {
      checkConnection()
    }
  }, [isOpen, checkConnection])

  const sendMessage = async () => {
    if (!inputValue.trim()) return

    const userMessage = {
      id: Date.now(),
      content: inputValue,
      isUser: true,
      timestamp: new Date().toISOString()
    }

    const currentInput = inputValue
    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    try {
      const response = await authenticatedFetch(`${apiUrl}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          message: currentInput,
          conversationId: conversationId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const { jobId } = await response.json()
      
      const statusMessageId = Date.now() + 1
      const statusMessage = {
        id: statusMessageId,
        content: 'Processing your request...',
        isUser: false,
        timestamp: new Date().toISOString(),
        isStatus: true
      }
      setMessages(prev => [...prev, statusMessage])
      
      let attempts = 0
      const maxAttempts = 60
      const pollInterval = 2000
      let lastFunMessageIndex = -1

      const pollForResult = async () => {
        attempts++

        try {
          const statusResponse = await authenticatedFetch(`${apiUrl}/chat/status/${jobId}`)

          if (!statusResponse.ok) {
            throw new Error(`Failed to get job status: ${statusResponse.statusText}`)
          }

          const statusData = await statusResponse.json()

          if (statusData.status === 'completed') {
            const { result } = statusData
            
            if (result.conversationId) {
              setConversationId(result.conversationId)
            }
            
            setMessages(prev => prev.map(msg => 
              msg.id === statusMessageId
                ? {
                    ...msg,
                    content: result.response,
                    isStatus: false,
                    timestamp: result.timestamp
                  }
                : msg
            ))
            
            setIsTyping(false)
            
          } else if (statusData.status === 'failed') {
            setMessages(prev => prev.map(msg => 
              msg.id === statusMessageId
                ? {
                    ...msg,
                    content: statusData.error || 'Request failed. Please try again.',
                    isError: true,
                    isStatus: false
                  }
                : msg
            ))
            
            setIsTyping(false)
            
          } else if (attempts >= maxAttempts) {
            const timeoutMessages = [
              '🎯 Mission incomplete. Ready for round two?',
              '🎮 Level timeout reached. Press restart?',
              '⏰ Time\'s up! Want to give it another shot?',
              '🔄 Circuit breaker activated. Try again?',
              '🎪 Show\'s intermission time. Encore?'
            ]
            
            setMessages(prev => prev.map(msg => 
              msg.id === statusMessageId
                ? {
                    ...msg,
                    content: timeoutMessages[Math.floor(Math.random() * timeoutMessages.length)],
                    isError: true,
                    isStatus: false
                  }
                : msg
            ))
            
            setIsTyping(false)
            
          } else {
            let statusText = 'Processing your request...'

            if (attempts >= 5) {
              const funMessages = [
                '🔮 Consulting the crystal ball...',
                '☕ Brewing the perfect response...',
                '🧠 Neurons firing at maximum capacity...',
                '📚 Reading through ancient scrolls...',
                '🎯 Calibrating answer precision...',
                '🌟 Gathering cosmic wisdom...',
                '🔬 Analyzing quantum possibilities...',
                '🎨 Painting your answer with words...',
                '🚀 Launching thought rockets...',
                '🧪 Mixing the perfect solution...',
                '🎭 Rehearsing the perfect response...',
                '🏗️ Building bridges to understanding...',
                '🌈 Following the rainbow to answers...',
                '🔍 Investigating all possibilities...',
                '⚡ Charging up the brain cells...',
                '🎪 Juggling multiple ideas...',
                '🎯 Aiming for the perfect answer...',
                '🏃 Racing through knowledge bases...',
                '🧩 Putting the pieces together...',
                '🎲 Rolling for intelligence...',
                '🍳 Cooking up something special...',
                '🎸 Tuning the response frequency...',
                '🌊 Surfing the waves of information...',
                '🎮 Loading next level insights...',
                '🦾 Flexing mental muscles...',
                '🎪 Performing mental gymnastics...',
                '🔧 Fine-tuning the response...',
                '🌌 Exploring the universe of knowledge...',
                '🎨 Crafting a masterpiece answer...',
                '🚁 Hovering over the solution...'
              ]

              const messageChangeInterval = Math.floor((attempts - 5) / 7.5)
              let messageIndex = messageChangeInterval % funMessages.length

              if (messageIndex === lastFunMessageIndex) {
                messageIndex = (messageIndex + 1) % funMessages.length
              }
              lastFunMessageIndex = messageIndex

              statusText = funMessages[messageIndex]
            } else if (attempts >= 3) {
              statusText = 'Still working on it...'
            }
            
            setMessages(prev => prev.map(msg => 
              msg.id === statusMessageId
                ? { ...msg, content: statusText }
                : msg
            ))
            
            setTimeout(pollForResult, pollInterval)
          }
          
        } catch (error) {
          console.error('Polling error:', error)
          
          if (attempts >= maxAttempts || error.message.includes('Job not found')) {
            setMessages(prev => prev.map(msg => 
              msg.id === statusMessageId
                ? {
                    ...msg,
                    content: `Error: ${error.message}`,
                    isError: true,
                    isStatus: false
                  }
                : msg
            ))
            
            setIsTyping(false)
          } else {
            setTimeout(pollForResult, pollInterval)
          }
        }
      }
      
      setTimeout(pollForResult, pollInterval)
      
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        content: `Connection error: ${error.message}. Please check your connection.`,
        isUser: false,
        timestamp: new Date().toISOString(),
        isError: true
      }
      setMessages(prev => [...prev, errorMessage])
      setIsTyping(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatMessage = (content) => {
    if (typeof content === 'object') {
      return (
        <div>
          <strong>📊 Query Results:</strong>
          <pre style={{ 
            background: 'var(--color-bg-gray)',
            color: 'var(--color-text-primary)',
            padding: '10px', 
            borderRadius: '5px', 
            fontSize: 'var(--font-size-sm)',
            marginTop: '8px',
            overflow: 'auto'
          }}>
            {JSON.stringify(content, null, 2)}
          </pre>
        </div>
      )
    }

    if (typeof content !== 'string') return content

    const parts = content.split(/(\d+\.\s\*\*[^*]+\*\*:?)/)
    
    return (
      <div style={{ lineHeight: '1.6' }}>
        {parts.map((part, index) => {
          if (/^\d+\.\s\*\*/.test(part)) {
            const match = part.match(/^(\d+\.\s)\*\*([^*]+)\*\*:?\s*(.*)$/s)
            if (match) {
              const [, number, title, description] = match
              return (
                <div key={index} style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    fontWeight: '600', 
                    color: 'var(--color-deep-blue)',
                    marginBottom: '4px'
                  }}>
                    {number}<strong>{title}</strong>
                  </div>
                  {description && (
                    <div style={{
                      marginLeft: '20px',
                      color: 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      {description.trim()}
                    </div>
                  )}
                </div>
              )
            }
          }
          
          else if (part.trim()) {
            const boldParts = part.split(/(\*\*[^*]+\*\*)/)
            return (
              <div key={index} style={{ marginBottom: '8px' }}>
                {boldParts.map((boldPart, boldIndex) => {
                  if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                    return (
                      <strong key={boldIndex} style={{ color: 'var(--color-deep-blue)' }}>
                        {boldPart.slice(2, -2)}
                      </strong>
                    )
                  }
                  return boldPart
                })}
              </div>
            )
          }
          
          return null
        })}
      </div>
    )
  }

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        className={`${styles.chatToggle} ${isOpen ? styles.open : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Veta Assistant"
        aria-label={isOpen ? 'Close Veta Assistant chat' : 'Open Veta Assistant chat'}
      >
        <FontAwesomeIcon
          icon={isOpen ? 'times' : 'comment-dots'}
          className={styles.chatToggleIcon}
        />
        {!isConnected && (
          <span className={`${styles.chatToggleBadge} ${styles.disconnected}`} />
        )}
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div
          className="mcp-chat-widget"
          style={{
            position: 'fixed',
            bottom: '114px',
            right: '24px',
            width: '380px',
            height: '520px',
            background: 'var(--color-bg-white)',
            borderRadius: '15px',
            boxShadow: '0 20px 40px rgba(30, 58, 95, 0.15), 0 4px 20px rgba(232, 54, 75, 0.08)',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid var(--color-border-light)'
          }}
        >
          {/* Header */}
          <div
            style={{
              background: 'var(--color-deep-blue-bg)',
              color: 'white',
              padding: '16px 20px',
              fontSize: 'var(--font-size-md)',
              fontWeight: '500',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>Veta Assistant</span>
            <div style={{ 
              fontSize: 'var(--font-size-sm)',
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <div 
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: isConnected ? colors.status.success : colors.status.error
                }}
              ></div>
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              background: 'var(--color-bg-light)'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  marginBottom: '15px',
                  display: 'flex',
                  flexDirection: message.isUser ? 'row-reverse' : 'row',
                  alignItems: 'flex-start'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: message.isUser ? colors.ui.tealPrimary : colors.ui.deepBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    margin: message.isUser ? '0 0 0 12px' : '0 12px 0 0',
                    flexShrink: 0
                  }}
                >
                  {message.isUser ? 'U' : 'V'}
                </div>
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '12px 16px',
                    borderRadius: '16px',
                    fontSize: 'var(--font-size-base)',
                    lineHeight: '1.5',
                    background: message.isUser
                      ? colors.ui.tealPrimary
                      : (message.isError ? 'var(--color-bg-light)' : 'var(--color-bg-white)'),
                    color: message.isUser
                      ? 'white'
                      : (message.isError ? colors.status.error : 'var(--color-text-primary)'),
                    border: !message.isUser ? '1px solid var(--color-border-light)' : 'none',
                    borderBottomRightRadius: message.isUser ? '6px' : '16px',
                    borderBottomLeftRadius: message.isUser ? '16px' : '6px',
                    boxShadow: message.isUser 
                      ? 'none' 
                      : '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {formatMessage(message.content)}
                  <div
                    style={{
                      fontSize: 'var(--font-size-2xs)',
                      opacity: 0.6,
                      marginTop: '5px',
                      textAlign: message.isUser ? 'right' : 'left'
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: colors.ui.deepBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: '600',
                    marginRight: '12px',
                    flexShrink: 0
                  }}
                >
                  V
                </div>
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'var(--color-bg-white)',
                    borderRadius: '16px',
                    border: '1px solid var(--color-border-light)',
                    borderBottomLeftRadius: '6px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div className={styles.typingDot}></div>
                    <div className={styles.typingDot}></div>
                    <div className={styles.typingDot}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: '16px 20px',
              background: 'var(--color-bg-white)',
              borderTop: '1px solid var(--color-border-light)',
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px'
            }}
          >
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me about your dashboard..."
                style={{
                  flex: 1,
                  minHeight: '40px',
                  maxHeight: '100px',
                  padding: '10px 16px',
                  border: '2px solid var(--color-border-light)',
                  borderRadius: '20px',
                  fontSize: 'var(--font-size-base)',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s ease',
                  background: 'var(--color-bg-white)',
                  color: 'var(--color-text-primary)'
                }}
                rows="1"
                onFocus={(e) => {
                  e.target.style.borderColor = colors.ui.tealPrimary
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--color-border-light)'
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isTyping}
                style={{
                  width: '40px',
                  height: '40px',
                  background: inputValue.trim() && !isTyping ? colors.ui.tealPrimary : colors.ui.neutralGray,
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: inputValue.trim() && !isTyping ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-md)',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (inputValue.trim() && !isTyping) {
                    e.target.style.transform = 'scale(1.05)'
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)'
                }}
              >
                ➤
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  )
}

MCPChatWidget.propTypes = {
  apiUrl: PropTypes.string
}

export default MCPChatWidget